/**
 * Service for fetching author data from the backend
 */

import { API_URL } from '../config/api';

/**
 * Fetches all authors from the backend with pagination
 */
export const fetchAuthors = async (page = 0, size = 12) => {
    try {
        const params = new URLSearchParams({
            page: page.toString(),
            size: size.toString()
        });

        const response = await fetch(`${API_URL}/authors?${params}`, {
            credentials: 'include',
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch authors: ${response.status} ${response.statusText}`);
        }

        return response.json();
    } catch (error) {
        console.error('Error fetching authors:', error);
        throw error;
    }
};

/**
 * Fetches a single author by ID from the backend
 */
export const fetchAuthorById = async (id) => {
    try {
        const response = await fetch(`${API_URL}/authors/${id}`, {
            credentials: 'include',
        });

        if (!response.ok) {
            if (response.status === 404) {
                throw new Error(`Author with ID ${id} not found`);
            }
            throw new Error(`Failed to fetch author: ${response.status} ${response.statusText}`);
        }

        return response.json();
    } catch (error) {
        console.error(`Error fetching author ${id}:`, error);
        throw error;
    }
};

/**
 * Fetches a simplified author by ID from the backend
 */
export const fetchSimplifiedAuthorById = async (id) => {
    try {
        const response = await fetch(`${API_URL}/authors/simplified/${id}`, {
            credentials: 'include',
        });

        if (!response.ok) {
            if (response.status === 404) {
                throw new Error(`Author with ID ${id} not found`);
            }
            throw new Error(`Failed to fetch simplified author: ${response.status} ${response.statusText}`);
        }

        return response.json();
    } catch (error) {
        console.error(`Error fetching simplified author ${id}:`, error);
        throw error;
    }
};

/**
 * Creates a new author on the backend
 */
export const createAuthor = async (authorData) => {
    try {
        const response = await fetch(`${API_URL}/authors`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(authorData),
        });

        if (!response.ok) {
            throw new Error(`Failed to create author: ${response.status} ${response.statusText}`);
        }

        return response.json();
    } catch (error) {
        console.error('Error creating author:', error);
        throw error;
    }
};

/**
 * Updates an existing author on the backend
 */
export const updateAuthor = async (id, authorData) => {
    try {
        const response = await fetch(`${API_URL}/authors/${id}`, {
            method: 'PUT',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(authorData),
        });

        if (!response.ok) {
            throw new Error(`Failed to update author: ${response.status} ${response.statusText}`);
        }

        return response.json();
    } catch (error) {
        console.error(`Error updating author ${id}:`, error);
        throw error;
    }
};

/**
 * Deletes an author from the backend
 */
export const deleteAuthor = async (id) => {
    try {
        const response = await fetch(`${API_URL}/authors/${id}`, {
            method: 'DELETE',
            credentials: 'include',
        });

        if (!response.ok) {
            throw new Error(`Failed to delete author: ${response.status} ${response.statusText}`);
        }
    } catch (error) {
        console.error(`Error deleting author ${id}:`, error);
        throw error;
    }
};

/**
 * Searches for authors by name
 */
export const searchAuthors = async (searchTerm, page = 0, size = 12) => {
    try {
        const params = new URLSearchParams({
            name: searchTerm,
            page: page.toString(),
            size: size.toString()
        });

        const response = await fetch(`${API_URL}/authors/search?${params}`, {
            credentials: 'include',
        });

        if (!response.ok) {
            throw new Error(`Failed to search authors: ${response.status} ${response.statusText}`);
        }

        return response.json();
    } catch (error) {
        console.error('Error searching authors:', error);
        throw error;
    }
};
